import { SYNC } from '@trips/shared/config';
import type { PullResponse, PushResponse, SyncRecordMap, SyncTable } from '@trips/shared/schema';
import { CLIENT_WRITABLE_TABLES, OPT_IN_TABLES } from '@trips/shared/schema-consts';
import type { Table } from 'dexie';
import { ApiError, api, isOffline, socketUrl } from '../lib/api';
import { db, kvGet, kvSet } from './db';
import { loadPending, onLocalWrite, pendingByRow, rebase, unindexPending } from './mutate';
import { getState, setSync, upsertRecords } from './store';

/**
 * Background sync: push queued edits, pull everything newer than our cursor, and listen on
 * a WebSocket for "something changed" so other devices' edits show up right away.
 */

const writable = new Set<string>(CLIENT_WRITABLE_TABLES);
let cursor = 0;
let running = false;
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> | null = null;
let again = false;
let socket: WebSocket | null = null;
let socketRetry = 0;
let socketTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;

export async function startSync() {
  if (running) return;
  running = true;
  cursor = (await kvGet<number>('sync.cursor')) ?? 0;
  await loadPending();
  onLocalWrite(() => schedulePush());
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') void syncNow();
  }, 5 * 60_000);
  void syncNow();
  connectSocket();
}

export function stopSync() {
  running = false;
  window.removeEventListener('online', onOnline);
  document.removeEventListener('visibilitychange', onVisible);
  if (pollTimer) clearInterval(pollTimer);
  closeSocket();
}

function onOnline() {
  void syncNow();
  connectSocket();
}

function onVisible() {
  if (document.visibilityState === 'visible') {
    void syncNow();
    if (!socket) connectSocket();
  }
}

export function schedulePush(delay = 250) {
  if (!running) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => void syncNow(), delay);
}

const refusedListeners = new Set<(reason: string) => void>();

/** Called when the server refuses an edit. The edit is dropped, so the UI should say so. */
export function onRefused(fn: (reason: string) => void): () => void {
  refusedListeners.add(fn);
  return () => refusedListeners.delete(fn);
}

/** Pushes then pulls. Concurrent calls coalesce into one follow-up run. */
export function syncNow(): Promise<void> {
  if (inFlight) {
    again = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      let refused: string | null = null;
      do {
        again = false;
        setSync({ state: 'syncing' });
        refused = (await push()) ?? refused;
        await pull();
      } while (again);
      setSync({
        state: 'idle',
        lastSyncAt: Date.now(),
        error: refused && `Some changes couldn't be saved: ${refused}`,
      });
    } catch (err) {
      if (isOffline(err)) setSync({ state: 'offline' });
      else setSync({ state: 'error', error: err instanceof Error ? err.message : String(err) });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Sends the outbox in order. Returns why the server refused an edit, if it did. */
async function push(): Promise<string | null> {
  let refused: string | null = null;
  for (;;) {
    const batch = await db.outbox.orderBy('seq').limit(SYNC.pushBatchSize).toArray();
    if (batch.length === 0) return refused;
    const res = await api<PushResponse>('/api/sync/push', {
      body: {
        mutations: batch.map(({ id, table, rowId, op, data, updatedAt }) => ({
          id,
          table,
          rowId,
          op,
          data,
          updatedAt,
        })),
      },
    });
    const done = new Set([...res.accepted, ...res.rejected.map((r) => r.id)]);
    await db.outbox.bulkDelete([...done]);
    unindexPending(done);
    setSync({ pending: await db.outbox.count() });
    if (res.rejected.length > 0) {
      console.warn('Sync rejected edits', res.rejected);
      refused ??= res.rejected[0]!.reason;
      for (const l of refusedListeners) l(res.rejected[0]!.reason);
    }
    if (batch.length < SYNC.pushBatchSize) return refused;
  }
}

/** Newer tables this build knows; the server sends them only when asked. */
const WITH = OPT_IN_TABLES.join(',');

async function pull() {
  await backfill();
  for (let page = 0; page < 200; page++) {
    const res = await api<PullResponse>(`/api/sync/pull?since=${cursor}&with=${WITH}`);
    await applyChanges(res.changes);
    cursor = res.cursor;
    await kvSet('sync.cursor', cursor);
    if (!res.more) return;
  }
}

/**
 * A device that synced before a table existed has its cursor past that table's rows: fetch
 * those tables from the start once.
 */
async function backfill() {
  for (const table of OPT_IN_TABLES) {
    const key = `sync.backfilled.${table}`;
    if (cursor === 0 || (await kvGet<boolean>(key))) continue;
    let since = 0;
    for (let page = 0; page < 200; page++) {
      const res = await api<PullResponse>(`/api/sync/pull?since=${since}&only=${table}`);
      await applyChanges(res.changes);
      since = res.cursor;
      if (!res.more) break;
    }
    await kvSet(key, true);
  }
}

export async function applyChanges(changes: PullResponse['changes']) {
  for (const [table, rows] of Object.entries(changes) as [
    SyncTable,
    SyncRecordMap[SyncTable][],
  ][]) {
    if (!rows?.length) continue;
    const merged = writable.has(table)
      ? rows.map((r) => (pendingByRow.size ? rebase(table as never, r as never) : r))
      : rows;
    await (db.records(table) as unknown as Table<unknown, string>).bulkPut(merged);
    upsertRecords(table, merged as never[]);
  }
}

// ---------- WebSocket nudges ----------

async function connectSocket() {
  if (!running || socket || typeof WebSocket === 'undefined') return;
  if (socketTimer) {
    clearTimeout(socketTimer);
    socketTimer = null;
  }
  let ticket: string;
  try {
    ticket = (await api<{ ticket: string }>('/api/sync/ticket', { body: {} })).ticket;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return;
    return retrySocket();
  }
  const ws = new WebSocket(socketUrl(`/api/sync/socket?ticket=${encodeURIComponent(ticket)}`));
  socket = ws;
  ws.onopen = () => {
    socketRetry = 0;
    pingTimer = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), 45_000);
  };
  ws.onmessage = (ev) => {
    if (typeof ev.data !== 'string' || ev.data === 'pong') return;
    try {
      const msg = JSON.parse(ev.data) as { type: string; rev?: number };
      if (msg.type === 'rev' && (msg.rev ?? 0) > cursor) void syncNow();
    } catch {
      // Ignore malformed messages.
    }
  };
  ws.onclose = () => {
    if (pingTimer) clearInterval(pingTimer);
    socket = null;
    retrySocket();
  };
}

function retrySocket() {
  if (!running) return;
  const delay = Math.min(60_000, 1000 * 2 ** socketRetry++);
  socketTimer = setTimeout(() => void connectSocket(), delay);
}

function closeSocket() {
  if (socketTimer) clearTimeout(socketTimer);
  if (pingTimer) clearInterval(pingTimer);
  const ws = socket;
  socket = null;
  ws?.close();
}

/** Clears local data, for sign-out on a revoked device. */
export async function wipeLocalData() {
  stopSync();
  await db.delete();
  location.reload();
}

export function syncState() {
  return getState().sync;
}
